import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';

const fmt = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return String(value);
  }
};

const actionTone = {
  PUBLISH: 'bg-green-50 text-green-700 border-green-100',
  PUBLISH_WITH_WARNING: 'bg-amber-50 text-amber-700 border-amber-100',
  HUMAN_REVIEW: 'bg-orange-50 text-orange-700 border-orange-100',
  REJECT: 'bg-red-50 text-red-700 border-red-100',
};

const detailTitle = {
  trend: 'Trending artist detail',
  report: 'Monthly report detail',
  moderation: 'Moderation detail',
  nudge: 'Nudge detail',
};

const AdminAiInsights = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [trending, setTrending] = useState([]);
  const [reports, setReports] = useState([]);
  const [moderation, setModeration] = useState([]);
  const [nudges, setNudges] = useState([]);
  const [reportArtistId, setReportArtistId] = useState('');
  const [moderationFilter, setModerationFilter] = useState('all');
  const [nudgeFilter, setNudgeFilter] = useState('all');
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [selectedNudges, setSelectedNudges] = useState([]);

  const isNudgeSelected = (item) => {
    const key = item.id || `${item.artist_id}-${item.queued_at}`;
    return selectedNudges.some((entry) => entry.key === key);
  };

  const loadInsights = async () => {
    setLoading(true);
    setError('');
    const readValue = (result) => (result.status === 'fulfilled' ? result.value : null);
    try {
      const [trendingResult, reportsResult, moderationResult, nudgesResult] = await Promise.allSettled([
        adminApi.get('/admin/ai/trending-cache', { params: { limit: 12 } }),
        adminApi.get('/admin/ai/monthly-reports', {
          params: {
            limit: 16,
            ...(reportArtistId.trim() ? { artist_id: reportArtistId.trim() } : {}),
          },
        }),
        adminApi.get('/admin/ai/moderation-logs', {
          params: { limit: 16, action: moderationFilter },
        }),
        adminApi.get('/admin/ai/nudges', {
          params: { limit: 16, sent: nudgeFilter },
        }),
      ]);
      const trendingResponse = readValue(trendingResult);
      const reportsResponse = readValue(reportsResult);
      const moderationResponse = readValue(moderationResult);
      const nudgesResponse = readValue(nudgesResult);
      setTrending(trendingResponse?.data?.records || []);
      setReports(reportsResponse?.data?.reports || []);
      setModeration(moderationResponse?.data?.records || []);
      setNudges(nudgesResponse?.data?.records || []);
      const unavailable = [
        !trendingResponse && 'trending artists',
        !reportsResponse && 'monthly reports',
        !moderationResponse && 'moderation logs',
        !nudgesResponse && 'nudges queue',
      ].filter(Boolean);
      setMessage('');
      setError(
        unavailable.length
          ? `Some AI insight feeds are not live yet: ${unavailable.join(', ')}. Existing panels stay usable and will fill as backend jobs create data.`
          : ''
      );
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'AI insights could not load right now.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInsights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moderationFilter, nudgeFilter]);

  const triggerTrendingRefresh = async () => {
    setBusy('trending');
    setError('');
    setMessage('');
    try {
      await adminApi.get('/feed/trending', { params: { limit: 10 } });
      await loadInsights();
      setMessage('Trending artist ranking refreshed.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not refresh trending artists.');
    } finally {
      setBusy('');
    }
  };

  const triggerBulkReports = async () => {
    setBusy('reports');
    setError('');
    setMessage('');
    try {
      await adminApi.post('/artist/monthly-report/bulk', {});
      await loadInsights();
      setMessage('Monthly report generation started. New reports will appear as they complete.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not start monthly reports.');
    } finally {
      setBusy('');
    }
  };

  const triggerBulkNudges = async () => {
    setBusy('nudges');
    setError('');
    setMessage('');
    try {
      await adminApi.post('/engagement/nudge/bulk', {});
      await loadInsights();
      setMessage('Inactivity nudge scan started.');
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not start inactivity nudges.');
    } finally {
      setBusy('');
    }
  };

  const toggleNudgeSelection = (item) => {
    const key = item.id || `${item.artist_id}-${item.queued_at}`;
    setSelectedNudges((current) =>
      current.some((entry) => entry.key === key)
        ? current.filter((entry) => entry.key !== key)
        : [...current, { key, id: item.id, artist_id: item.artist_id, queued_at: item.queued_at }]
    );
  };

  const resendSelectedNudges = async (items) => {
    if (!items.length) {
      setError('Choose at least one nudge to resend.');
      return;
    }
    setBusy('resend-nudges');
    setError('');
    setMessage('');
    try {
      const response = await adminApi.post('/admin/ai/nudges/resend', {
        items: items.map((item) => ({
          id: item.id || undefined,
          artist_id: item.artist_id || undefined,
          queued_at: item.queued_at || undefined,
        })),
      });
      await loadInsights();
      setSelectedNudges([]);
      setMessage(`Nudge resend queued. Updated: ${response.data?.updated ?? 0}, skipped: ${response.data?.skipped ?? 0}.`);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Could not queue nudge resend.');
    } finally {
      setBusy('');
    }
  };

  const summary = useMemo(() => {
    const trendingArtists = trending.flatMap((entry) => entry.artists || []);
    const pendingModeration = moderation.filter((item) => item.action === 'HUMAN_REVIEW').length;
    const pendingNudges = nudges.filter((item) => !item.sent).length;
    return {
      trendingArtists: trendingArtists.length,
      reports: reports.length,
      pendingModeration,
      pendingNudges,
    };
  }, [trending, reports, moderation, nudges]);

  const renderDetailPanel = () => {
    if (!selectedDetail) {
      return (
        <div className="rounded-[1.75rem] border border-dashed border-orange-200 bg-orange-50/40 p-5 text-sm font-semibold leading-7 text-gray-600">
          Select any trend, report, moderation item, or nudge from below to inspect the full AI context here.
        </div>
      );
    }

    if (selectedDetail.type === 'trend') {
      const artist = selectedDetail.record;
      return (
        <div className="grid gap-4 lg:grid-cols-[1.25fr_0.75fr]">
          <div className="space-y-3">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#FF6F00]">Artist</p>
              <h3 className="mt-2 text-2xl font-black text-gray-950">{artist.artist_name}</h3>
              <p className="mt-1 text-sm font-semibold text-gray-600">{artist.category || 'Category not tagged yet'}</p>
            </div>
            <div className="rounded-2xl border border-orange-100 bg-orange-50/50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#4A148C]">Why AI ranked this artist</p>
              <p className="mt-2 text-sm leading-7 text-gray-800">{artist.reason || 'No AI reason saved yet.'}</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Trend label</p>
                <p className="mt-2 text-base font-black text-gray-900">{artist.trend_label || '-'}</p>
              </div>
              <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Rank and score</p>
                <p className="mt-2 text-base font-black text-gray-900">#{artist.rank} • {artist.trend_score}</p>
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Suggested admin actions</p>
            <ul className="mt-3 space-y-2 text-sm leading-7 text-gray-700">
              <li>Review profile freshness and recent uploads.</li>
              <li>Check if this artist deserves spotlight or feature placement.</li>
              <li>Verify whether trend reasons align with the public artist card.</li>
            </ul>
            {artist.artist_id ? (
              <Link to={`/admin/artists?focus=${encodeURIComponent(artist.artist_id)}`} className="btn-outline mt-4 inline-flex">
                Open artist detail
              </Link>
            ) : null}
          </div>
        </div>
      );
    }

    if (selectedDetail.type === 'report') {
      const report = selectedDetail.record;
      return (
        <div className="grid gap-4 lg:grid-cols-[1.35fr_0.65fr]">
          <div>
            <h3 className="text-2xl font-black text-gray-950">{report.artist_name}</h3>
            <p className="mt-1 text-sm font-semibold text-gray-600">
              {report.month}/{report.year} • Generated {fmt(report.generated_at)}
            </p>
            <div className="mt-4 rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#4A148C]">Full AI report</p>
              <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-800">{report.report_text || 'No report text saved yet.'}</p>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Snapshot metrics</p>
            <div className="mt-3 grid gap-2 text-sm font-semibold text-gray-700">
              <div>Posts: {report.metrics_snapshot?.posts_count ?? 0}</div>
              <div>Views: {report.metrics_snapshot?.views ?? 0}</div>
              <div>Likes: {report.metrics_snapshot?.likes ?? 0}</div>
              <div>Profile visits: {report.metrics_snapshot?.profile_visits ?? 0}</div>
              <div>Gig inquiries: {report.metrics_snapshot?.gig_inquiries ?? 0}</div>
              <div>Followers: {report.metrics_snapshot?.new_followers ?? 0}</div>
            </div>
            {report.artist_id ? (
              <Link
                to={`/admin/artists?focus=${encodeURIComponent(report.artist_id)}`}
                className="btn-outline mt-4 inline-flex"
              >
                Open artist detail
              </Link>
            ) : null}
          </div>
        </div>
      );
    }

    if (selectedDetail.type === 'moderation') {
      const item = selectedDetail.record;
      return (
        <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-xs font-black ${actionTone[item.action] || actionTone.HUMAN_REVIEW}`}>
                {item.action}
              </span>
              <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-black text-gray-700">{item.content_type}</span>
              <span className="text-xs font-semibold text-gray-500">{fmt(item.checked_at)}</span>
            </div>
            <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-[#4A148C]">Moderation verdict</p>
              <p className="mt-2 text-sm leading-7 text-gray-800">{item.verdict || 'No verdict saved.'}</p>
            </div>
            {!!(item.categories_flagged || []).length && (
              <div>
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Flagged categories</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.categories_flagged.map((flag) => (
                    <span key={flag} className="rounded-full border border-red-100 bg-red-50 px-3 py-1 text-xs font-black text-red-700">
                      {flag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {item.text_preview || item.context || item.image_url ? (
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Direct content preview</p>
                {item.text_preview ? <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-800">{item.text_preview}</p> : null}
                {item.context ? <p className="mt-3 text-sm leading-7 text-gray-600"><span className="font-black text-gray-900">Context:</span> {item.context}</p> : null}
                {item.image_url ? (
                  <a href={item.image_url} target="_blank" rel="noreferrer" className="mt-3 inline-flex rounded-full border border-orange-200 px-3 py-1 text-xs font-black text-orange-700">
                    Open source image
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Reference</p>
            <div className="mt-3 space-y-2">
              <p><span className="font-black text-gray-900">Content ID:</span> {item.content_id || '-'}</p>
              <p><span className="font-black text-gray-900">Artist ID:</span> {item.artist_id || '-'}</p>
              <p><span className="font-black text-gray-900">Action time:</span> {fmt(item.checked_at)}</p>
            </div>
            {item.artist_id ? (
              <Link
                to={`/admin/artists?focus=${encodeURIComponent(item.artist_id)}`}
                className="btn-outline mt-4 inline-flex"
              >
                Open artist detail
              </Link>
            ) : null}
          </div>
        </div>
      );
    }

    const item = selectedDetail.record;
    return (
      <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full border px-3 py-1 text-xs font-black ${item.sent ? 'border-green-100 bg-green-50 text-green-700' : 'border-orange-100 bg-orange-50 text-orange-700'}`}>
              {item.sent ? 'Sent' : 'Pending'}
            </span>
            <span className="text-xs font-semibold text-gray-500">{fmt(item.queued_at)}</span>
          </div>
          <div>
            <h3 className="text-2xl font-black text-gray-950">{item.artist_name}</h3>
            <p className="mt-1 text-sm font-semibold text-gray-600">
              {item.category || 'Category missing'} • {item.days_inactive ?? 0} inactive day(s)
            </p>
          </div>
          <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4 text-sm text-gray-800">
            <p><span className="font-black text-gray-900">Warm:</span> {item.nudge_variants?.warm || '-'}</p>
            <p className="mt-2"><span className="font-black text-gray-900">Curiosity:</span> {item.nudge_variants?.curiosity || '-'}</p>
            <p className="mt-2"><span className="font-black text-gray-900">Opportunity:</span> {item.nudge_variants?.opportunity || '-'}</p>
          </div>
        </div>
          <div className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
            <p className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Suggested admin action</p>
            <ul className="mt-3 space-y-2 text-sm leading-7 text-gray-700">
              <li>Check if the artist profile is still complete and public-ready.</li>
              <li>Verify the nudge tone before a resend if the artist is high priority.</li>
              <li>Use artist detail to see recent edits, trust, and growth notes.</li>
            </ul>
            <button
              type="button"
              className="btn-primary mt-4"
              disabled={busy === 'resend-nudges'}
              onClick={() =>
                resendSelectedNudges([
                  { id: item.id, artist_id: item.artist_id, queued_at: item.queued_at },
                ])
              }
            >
              {busy === 'resend-nudges' ? 'Queueing...' : 'Resend this nudge'}
            </button>
            {item.artist_id ? (
              <Link
                to={`/admin/artists?focus=${encodeURIComponent(item.artist_id)}`}
              className="btn-outline mt-4 inline-flex"
            >
              Open artist detail
            </Link>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm lg:p-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.35em] text-[#4A148C]">AI insights</p>
            <h1 className="mt-3 text-3xl font-black text-gray-950">Trends, reports, nudges, and moderation</h1>
            <p className="mt-3 max-w-3xl text-gray-600">
              One admin screen for the KalaManch AI modules that currently live in backend. Use this to monitor artist momentum, monthly summaries, moderation decisions, and re-engagement queues.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn-outline" onClick={loadInsights} disabled={loading}>
              {loading ? 'Refreshing...' : 'Refresh all'}
            </button>
            <Link to="/admin/ai-studio" className="btn-outline">
              Open AI Studio
            </Link>
            <Link to="/admin/trust" className="btn-outline">
              Open Trust Queue
            </Link>
          </div>
        </div>
      </section>

      {error && <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-3 font-bold text-red-700">{error}</div>}
      {message && <div className="rounded-2xl border border-green-200 bg-green-50 px-5 py-3 font-bold text-green-700">{message}</div>}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Trending artists cached</p>
          <p className="mt-2 text-3xl font-black text-gray-950">{summary.trendingArtists}</p>
        </div>
        <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Monthly reports loaded</p>
          <p className="mt-2 text-3xl font-black text-gray-950">{summary.reports}</p>
        </div>
        <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Moderation needs review</p>
          <p className="mt-2 text-3xl font-black text-gray-950">{summary.pendingModeration}</p>
        </div>
        <div className="rounded-[1.75rem] border border-purple-100 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-500">Pending nudges</p>
          <p className="mt-2 text-3xl font-black text-gray-950">{summary.pendingNudges}</p>
        </div>
      </section>

      <section className="grid gap-4 xl:grid-cols-4">
        <Link to="/admin/artists" className="rounded-[1.75rem] border border-orange-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Artist AI panel</p>
          <h2 className="mt-2 text-xl font-black text-gray-950">Open artist details</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Review AI summary, trust score, moderation notes, and monthly growth snapshots inside each artist record.</p>
        </Link>
        <Link to="/admin/competitions" className="rounded-[1.75rem] border border-orange-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Artwork review</p>
          <h2 className="mt-2 text-xl font-black text-gray-950">Competition AI review</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Review submission AI tags, correct them where needed, and publish clean artwork metadata through admin workflows.</p>
        </Link>
        <div className="rounded-[1.75rem] border border-orange-100 bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">What appears here</p>
          <h2 className="mt-2 text-xl font-black text-gray-950">Daily AI operations</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">This page surfaces the live backend queues already running for reports, trend ranking, moderation decisions, and re-engagement nudges.</p>
        </div>
        <div className="rounded-[1.75rem] border border-orange-100 bg-white p-5 shadow-sm">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Suggested admin rhythm</p>
          <h2 className="mt-2 text-xl font-black text-gray-950">Weekly + monthly flow</h2>
          <p className="mt-2 text-sm leading-6 text-gray-600">Refresh trending weekly, review moderation daily, generate monthly summaries in bulk, and run inactivity nudges whenever engagement slows down.</p>
        </div>
      </section>

      <section className="rounded-3xl border border-orange-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Inspector</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">
              {selectedDetail ? detailTitle[selectedDetail.type] || 'AI item detail' : 'AI detail viewer'}
            </h2>
            <p className="mt-2 text-sm leading-7 text-gray-600">
              Use this panel to inspect one AI result properly before taking a platform action.
            </p>
          </div>
          {selectedDetail ? (
            <button type="button" className="btn-outline" onClick={() => setSelectedDetail(null)}>
              Clear detail
            </button>
          ) : null}
        </div>
        <div className="mt-5">{renderDetailPanel()}</div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Momentum</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">Trending artists</h2>
          </div>
          <button type="button" className="btn-primary" onClick={triggerTrendingRefresh} disabled={busy === 'trending'}>
            {busy === 'trending' ? 'Refreshing...' : 'Refresh trend ranking'}
          </button>
        </div>
        <div className="mt-5 space-y-4">
          {loading ? (
            <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">Loading trending cache...</div>
          ) : trending.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">No trending cache yet.</div>
          ) : (
            trending.map((entry) => (
              <article key={entry.cache_key} className="rounded-3xl border border-gray-100 bg-gray-50 p-5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-black text-gray-900">{entry.cache_key}</p>
                    <p className="text-xs font-semibold text-gray-500">Generated {fmt(entry.generated_at)}</p>
                  </div>
                  <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-gray-700">{(entry.artists || []).length} artists</span>
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-2">
                  {(entry.artists || []).map((artist) => (
                    <div key={`${entry.cache_key}-${artist.artist_id}`} className="rounded-2xl border border-white bg-white p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-black text-gray-500">#{artist.rank}</p>
                          <p className="text-lg font-black text-gray-950">{artist.artist_name}</p>
                          <p className="text-sm font-semibold text-gray-600">{artist.category}</p>
                        </div>
                        <span className="rounded-full border border-orange-100 bg-orange-50 px-3 py-1 text-xs font-black text-orange-700">
                          {artist.trend_score}
                        </span>
                      </div>
                      <p className="mt-3 text-sm font-semibold text-gray-700">{artist.reason}</p>
                      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#4A148C]">{artist.trend_label}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="inline-flex rounded-full border border-purple-200 px-3 py-1 text-xs font-black text-[#4A148C]"
                          onClick={() => setSelectedDetail({ type: 'trend', record: artist })}
                        >
                          View detail
                        </button>
                        {artist.artist_id ? (
                          <Link to={`/admin/artists?focus=${encodeURIComponent(artist.artist_id)}`} className="inline-flex rounded-full border border-orange-200 px-3 py-1 text-xs font-black text-orange-700">
                            Open artist
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Performance</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">Monthly artist reports</h2>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Filter by artist ID</span>
              <input
                type="text"
                value={reportArtistId}
                onChange={(event) => setReportArtistId(event.target.value)}
                className="mt-2 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm sm:w-72"
                placeholder="Optional artist ID"
              />
            </label>
            <button type="button" className="btn-outline" onClick={loadInsights}>
              Apply filter
            </button>
            <button type="button" className="btn-primary" onClick={triggerBulkReports} disabled={busy === 'reports'}>
              {busy === 'reports' ? 'Starting...' : 'Generate bulk reports'}
            </button>
          </div>
        </div>
        <div className="mt-5 space-y-4">
          {loading ? (
            <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">Loading reports...</div>
          ) : reports.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">No monthly reports yet.</div>
          ) : (
            reports.map((report, index) => (
              <article key={`${report.artist_id}-${report.month}-${report.year}-${index}`} className="rounded-3xl border border-gray-100 bg-gray-50 p-5">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="text-xl font-black text-gray-950">{report.artist_name}</h3>
                    <p className="mt-1 text-sm font-semibold text-gray-600">
                      {report.month}/{report.year} • Generated {fmt(report.generated_at)}
                    </p>
                    <p className="mt-3 whitespace-pre-line text-sm leading-7 text-gray-800">{report.report_text}</p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="inline-flex rounded-full border border-purple-200 px-4 py-2 text-xs font-black text-[#4A148C]"
                        onClick={() => setSelectedDetail({ type: 'report', record: report })}
                      >
                        View detail
                      </button>
                      {report.artist_id ? (
                        <Link
                          to={`/admin/artists?focus=${encodeURIComponent(report.artist_id)}`}
                          className="inline-flex rounded-full border border-orange-200 px-4 py-2 text-xs font-black text-orange-700"
                        >
                          Open artist detail
                        </Link>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid min-w-[220px] gap-2 rounded-2xl border border-white bg-white p-4 text-sm font-semibold text-gray-700">
                    <div>Posts: {report.metrics_snapshot?.posts_count ?? 0}</div>
                    <div>Views: {report.metrics_snapshot?.views ?? 0}</div>
                    <div>Likes: {report.metrics_snapshot?.likes ?? 0}</div>
                    <div>Profile visits: {report.metrics_snapshot?.profile_visits ?? 0}</div>
                    <div>Gig inquiries: {report.metrics_snapshot?.gig_inquiries ?? 0}</div>
                    <div>Followers: {report.metrics_snapshot?.new_followers ?? 0}</div>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>
      </section>

      <section className="grid gap-8 xl:grid-cols-2">
        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Safety</p>
              <h2 className="mt-2 text-2xl font-black text-gray-950">Moderation logs</h2>
            </div>
            <label className="block">
              <span className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Action filter</span>
              <select
                value={moderationFilter}
                onChange={(event) => setModerationFilter(event.target.value)}
                className="mt-2 rounded-2xl border border-gray-200 px-4 py-3 text-sm"
              >
                <option value="all">All</option>
                <option value="PUBLISH">Publish</option>
                <option value="PUBLISH_WITH_WARNING">Publish with warning</option>
                <option value="HUMAN_REVIEW">Human review</option>
                <option value="REJECT">Reject</option>
              </select>
            </label>
          </div>
          <div className="mt-5 space-y-4">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">Loading moderation logs...</div>
            ) : moderation.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">No moderation logs yet.</div>
            ) : (
              moderation.map((item, index) => (
                <article key={`${item.content_id}-${index}`} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full border px-3 py-1 text-xs font-black ${actionTone[item.action] || actionTone.HUMAN_REVIEW}`}>
                      {item.action}
                    </span>
                    <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-gray-700">{item.content_type}</span>
                    <span className="text-xs font-semibold text-gray-500">{fmt(item.checked_at)}</span>
                  </div>
                  <p className="mt-3 text-sm font-black text-gray-900">{item.verdict}</p>
                  <p className="mt-2 text-sm text-gray-700">Content ID: {item.content_id}</p>
                  {item.artist_id && <p className="mt-1 text-sm text-gray-700">Artist ID: {item.artist_id}</p>}
                  {item.text_preview ? <p className="mt-3 text-sm leading-7 text-gray-700">{item.text_preview}</p> : null}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex rounded-full border border-purple-200 px-3 py-1 text-xs font-black text-[#4A148C]"
                      onClick={() => setSelectedDetail({ type: 'moderation', record: item })}
                    >
                      View detail
                    </button>
                    {item.artist_id ? (
                      <Link
                        to={`/admin/artists?focus=${encodeURIComponent(item.artist_id)}`}
                        className="inline-flex rounded-full border border-orange-200 px-3 py-1 text-xs font-black text-orange-700"
                      >
                        Open artist
                      </Link>
                    ) : null}
                  </div>
                  {!!(item.categories_flagged || []).length && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {item.categories_flagged.map((flag) => (
                        <span key={flag} className="rounded-full border border-red-100 bg-red-50 px-3 py-1 text-xs font-black text-red-700">
                          {flag}
                        </span>
                      ))}
                    </div>
                  )}
                </article>
              ))
            )}
          </div>
        </section>

        <section className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF6F00]">Re-engagement</p>
              <h2 className="mt-2 text-2xl font-black text-gray-950">Nudges queue</h2>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block">
                <span className="text-xs font-black uppercase tracking-[0.18em] text-gray-500">Queue filter</span>
                <select
                  value={nudgeFilter}
                  onChange={(event) => setNudgeFilter(event.target.value)}
                  className="mt-2 rounded-2xl border border-gray-200 px-4 py-3 text-sm"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                </select>
              </label>
              <button type="button" className="btn-primary" onClick={triggerBulkNudges} disabled={busy === 'nudges'}>
                {busy === 'nudges' ? 'Starting...' : 'Run inactivity scan'}
              </button>
              <button
                type="button"
                className="btn-outline"
                onClick={() => resendSelectedNudges(selectedNudges)}
                disabled={busy === 'resend-nudges' || selectedNudges.length === 0}
              >
                {busy === 'resend-nudges' ? 'Queueing...' : `Resend selected (${selectedNudges.length})`}
              </button>
            </div>
          </div>
          {selectedNudges.length ? (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-purple-100 bg-purple-50/50 px-4 py-3 text-sm font-semibold text-[#4A148C]">
              <span>{selectedNudges.length} nudge record(s) selected for resend.</span>
              <button
                type="button"
                className="inline-flex rounded-full border border-purple-200 bg-white px-3 py-1 text-xs font-black text-[#4A148C]"
                onClick={() => setSelectedNudges([])}
              >
                Clear selection
              </button>
            </div>
          ) : null}
          <div className="mt-5 space-y-4">
            {loading ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">Loading nudges...</div>
            ) : nudges.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-gray-200 p-6 text-center font-bold text-gray-500">No nudge records yet.</div>
            ) : (
              nudges.map((item, index) => (
                <article key={`${item.artist_id}-${item.queued_at}-${index}`} className="rounded-2xl border border-gray-100 bg-gray-50 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="mr-1 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-black text-gray-700">
                      <input
                        type="checkbox"
                        checked={isNudgeSelected(item)}
                        onChange={() => toggleNudgeSelection(item)}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                      Select
                    </label>
                    <span className={`rounded-full border px-3 py-1 text-xs font-black ${item.sent ? 'border-green-100 bg-green-50 text-green-700' : 'border-orange-100 bg-orange-50 text-orange-700'}`}>
                      {item.sent ? 'Sent' : 'Pending'}
                    </span>
                    <span className="text-xs font-semibold text-gray-500">{fmt(item.queued_at)}</span>
                  </div>
                  <p className="mt-3 text-lg font-black text-gray-950">{item.artist_name}</p>
                  <p className="mt-1 text-sm font-semibold text-gray-600">
                    {item.category} • {item.days_inactive} inactive day(s)
                  </p>
                  <div className="mt-3 space-y-2 rounded-2xl border border-white bg-white p-4 text-sm text-gray-700">
                    <p><span className="font-black text-gray-900">Warm:</span> {item.nudge_variants?.warm || '-'}</p>
                    <p><span className="font-black text-gray-900">Curiosity:</span> {item.nudge_variants?.curiosity || '-'}</p>
                    <p><span className="font-black text-gray-900">Opportunity:</span> {item.nudge_variants?.opportunity || '-'}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex rounded-full border border-purple-200 px-3 py-1 text-xs font-black text-[#4A148C]"
                      onClick={() => setSelectedDetail({ type: 'nudge', record: item })}
                    >
                      View detail
                    </button>
                    <button
                      type="button"
                      className="inline-flex rounded-full border border-green-200 px-3 py-1 text-xs font-black text-green-700"
                      disabled={busy === 'resend-nudges'}
                      onClick={() =>
                        resendSelectedNudges([
                          { id: item.id, artist_id: item.artist_id, queued_at: item.queued_at },
                        ])
                      }
                    >
                      {busy === 'resend-nudges' ? 'Queueing...' : 'Resend'}
                    </button>
                    {item.artist_id ? (
                      <Link
                        to={`/admin/artists?focus=${encodeURIComponent(item.artist_id)}`}
                        className="inline-flex rounded-full border border-orange-200 px-3 py-1 text-xs font-black text-orange-700"
                      >
                        Open artist
                      </Link>
                    ) : null}
                  </div>
                </article>
              ))
            )}
          </div>
        </section>
      </section>
    </div>
  );
};

export default AdminAiInsights;
